import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const STORAGE_KEY = 'arcus_token';

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [token, setToken]     = useState(() => localStorage.getItem(STORAGE_KEY));
  const [loading, setLoading] = useState(true);

  // Verify token on mount
  useEffect(() => {
    async function check() {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) { setLoading(false); return; }
      try {
        const res = await fetch('/api/auth/me', {
          headers: { Authorization: `Bearer ${stored}` }
        });
        if (res.ok) {
          const data = await res.json();
          setUser(data);
          setToken(stored);
        } else {
          localStorage.removeItem(STORAGE_KEY);
          setToken(null);
        }
      } catch {
        localStorage.removeItem(STORAGE_KEY);
        setToken(null);
      } finally { setLoading(false); }
    }
    check();
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erreur de connexion');
    localStorage.setItem(STORAGE_KEY, data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    const res = await fetch('/api/auth/change-password', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Erreur');
    return true;
  }, [token]);

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    logout,
    changePassword,
    isManager:      user?.role === 'manager',
    isSouscripteur: user?.role === 'souscripteur',
    isAccounts:     user?.role === 'accounts',
    isIT:           user?.role === 'it',
    isCourtier:     user?.role === 'courtier',
    isClient:       user?.role === 'client',
    isVoyageur:     user?.role === 'voyageur',
    isArcus:        ['manager','souscripteur','accounts','it'].includes(user?.role),
    // Phase D2 : Accounts a un accès en lecture seule à tout le CRM
    // (ses seules actions autorisées : marquer payé + générer Excel + bouton payé appel de fonds, gérés localement)
    isReadOnly:     user?.role === 'accounts',
    dossierId:      user?.dossier_id   || null,
    procedureId:    user?.procedure_id || null,
    clientId:       user?.client_id    || null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
